import React from "react";
import { useInView } from "react-intersection-observer";
import { useDownload } from "./DownloadContext";

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <path
      d="M5 12l5 5L20 7"
      stroke="#16A34A"
      strokeWidth="3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ShieldIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path
      d="M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z"
      stroke="#16A34A"
      strokeWidth="2"
      strokeLinejoin="round"
    />
    <path
      d="M9 12l2 2 4-4"
      stroke="#16A34A"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const TrustedSection = () => {
  const { ref: textRef, inView: textView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });
  const { ref: cardRef, inView: cardView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const {
    progress,
    downloadedMB,
    totalMB,
    isDownloading,
    isComplete,
    error,
    startDownload,
  } = useDownload();

  let statusText;
  if (error && !isDownloading) statusText = "Download started in browser";
  else if (isComplete) statusText = "Download complete";
  else if (isDownloading) statusText = `Downloading… ${Math.round(progress)}%`;
  else statusText = "Ready to download";

  const sizeText = totalMB
    ? `${downloadedMB} / ${totalMB} MB`
    : isDownloading
      ? `${downloadedMB} MB`
      : " ";

  return (
    <section className="w-full max-w-[1180px] mx-auto md:px-7 px-4 flex md:flex-row flex-col items-center gap-10">
      <div
        ref={textRef}
        className={`${textView ? "animate-slideRight z-[3] anim-preserve" : "opacity-0 z-0"
          } flex-1 flex flex-col gap-3`}
      >
        <h2 className="font-jakarta font-extrabold text-[#0F1B2D] text-left text-[2.625rem] md:text-[2.4rem] lg:text-[2.8rem] xl:text-[3.125rem] leading-[1.24] tracking-[-0.02em]">
          <span className="bg-clip-text text-transparent bg-gradient-to-r from-[#3AB1F6] to-[#407BFF]">
            Trusted Download.
          </span>{" "}
          Built for IT-cautious Windows.
        </h2>
        <p className="font-inter font-normal text-[#33425A] text-left text-[1.313rem] md:text-[1.15rem] lg:text-[1.2rem] xl:text-[1.438rem] leading-[1.5] max-w-[520px]">
          Downloading software directly should feel safe. Every box that makes
          Windows nervous, we've already ticked.
        </p>
      </div>

      <div
        ref={cardRef}
        className={`${cardView ? "animate-slideLeft z-[3] anim-preserve" : "opacity-0 z-0"
          } flex-1 w-full`}
      >
        <div
          role="button"
          tabIndex={0}
          onClick={() => !isDownloading && startDownload()}
          onKeyDown={(e) => {
            if ((e.key === "Enter" || e.key === " ") && !isDownloading) {
              e.preventDefault();
              startDownload();
            }
          }}
          className={`bg-white rounded-2xl border border-[#0000000D] shadow-sm p-5 flex flex-col gap-4 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3AB1F6] ${
            isDownloading ? "cursor-progress" : "cursor-pointer"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-xl shrink-0 flex items-center justify-center text-white"
              style={{
                background:
                  "linear-gradient(180deg, #3AB1F6 0%, #407BFF 100%)",
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 5.5 11 4v8H3V5.5zM12 3.85 21 2.5V12h-9V3.85zM3 13h8v7.15L3 19V13zm9 0h9v8.5L12 20.15V13z" />
              </svg>
            </div>
            <div className="flex flex-col flex-1 min-w-0">
              <span className="font-inter font-semibold text-[#0F1B2D] text-[0.95rem] truncate">
                ZeroTyping-Setup.msi
              </span>
              <span className="font-inter font-normal text-[#33425A] text-[0.8125rem]">
                Verified publisher · Zero Typing Ltd
              </span>
            </div>
            <ShieldIcon />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between text-[0.75rem] font-inter text-[#33425A]">
              <span>{statusText}</span>
              <span>{sizeText}</span>
            </div>
            <div className="w-full h-2 bg-[#E5E7EB] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-[width] duration-150 ease-out"
                style={{
                  width: `${progress}%`,
                  background:
                    "linear-gradient(90deg, #3AB1F6 0%, #407BFF 100%)",
                }}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {["SmartScreen passed", "Antivirus clean", "Win 10 & 11"].map(
              (label, i) => (
                <span
                  key={i}
                  className="flex items-center gap-1.5 bg-[#DCFCE7] text-[#16A34A] rounded-full px-3 py-1 text-[0.75rem] font-inter font-medium"
                >
                  <CheckIcon />
                  {label}
                </span>
              )
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedSection;
